// type reference workaround until we package compiled code
/// <reference types="../types/i18next-pseudo" />

import i18next, { InitOptions, ThirdPartyModule } from 'i18next';
import languageDetector from 'i18next-browser-languagedetector';
import Pseudo from 'i18next-pseudo';

import en from './resources/en.json';
import enUs from './resources/en-US.json';
import { formatFunction, PSEUDO_LOCALE_CODE } from './util/format';
import { dateHelperI18next, formatWithT, numbroI18next, setDocumentLanguage, stringifyPatch } from './modules';

const pseudo = new Pseudo({ languageToPseudo: PSEUDO_LOCALE_CODE });

const DEFAULT_OPTIONS: InitOptions = {
  initImmediate: false,
  debug: process.env.NODE_ENV !== 'production',
  fallbackLng: 'en-GB',

  resources: {
    en,
    'en-GB': en,
    'en-US': enUs,
    ...(process.env.NODE_ENV !== 'production' && {
      pseudo: {},
    }),
  },

  keySeparator: false,
  nsSeparator: false,

  interpolation: {
    escapeValue: false, // React already escapes
    format: formatFunction,
    alwaysFormat: true,
  },

  postProcess: [pseudo.name],
};

/**
 * Initialise the i18n instance. Should be called once at the top of an app, then passed down through context.
 *
 * Do not use this to access the i18n instance in application code!
 * It can be used in test code as the instance is isolated anyway.
 *
 * @param options Initial options
 */
export const initI18n = (options: InitOptions, ...additionalModules: ThirdPartyModule[]) => {
  const i18n = i18next
    .createInstance(DEFAULT_OPTIONS)
    .use(languageDetector)
    .use(pseudo)
    .use(dateHelperI18next)
    .use(numbroI18next)
    .use(formatWithT)
    .use(stringifyPatch)
    .use(setDocumentLanguage);
  additionalModules.forEach((module) => i18n.use(module));
  i18n.init({ ...options, detection: { caches: [] } });
  return i18n;
};

// Allows passing an object as children to HTML elements to allow interpolation inside `Trans`
declare module 'react-i18next' {
  interface CustomTypeOptions {
    allowObjectInHTMLChildren: true;
  }
}
